import { useEffect, useState, type FormEvent } from "react"
import { Archive, Check, FileImage, PackagePlus, Sparkles, Upload } from "lucide-react"
import { GalleryCanvasScene } from "@nova-ai/gallery-canvas"

import {
  useVisualDirection,
  useVisualDirectionMutations,
  useVisualDirectionMoodboardSample,
  useVisualDirectionProposals,
  useVisualDirectionFontGlyphCoverage,
  useVisualDirections,
  type VisualDirection,
  type VisualDirectionAsset,
  type VisualDirectionAssetKind,
  type VisualDirectionDraft,
  type VisualDirectionPalette,
  type VisualDirectionProposal,
  type VisualDirectionVersion,
} from "../../services/use-visual-directions"
import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { Combobox } from "../ui/combobox"
import { FormField } from "../ui/form-field"
import { TextInput } from "../ui/text-input"

const ASSET_KINDS: { value: VisualDirectionAssetKind; label: string }[] = [
  { value: "font", label: "Font" },
  { value: "logo", label: "Logo" },
  { value: "icon", label: "Icon" },
  { value: "badge", label: "Badge" },
  { value: "decoration", label: "Decoration" },
  { value: "style_image", label: "Style reference" },
  { value: "model_reference", label: "Human-model reference" },
]

export function VisualDirectionsView({
  previewConfigSku,
  onPreviewConfigSkuChange,
}: {
  previewConfigSku: string
  onPreviewConfigSkuChange: (value: string) => void
}) {
  const [includeArchived, setIncludeArchived] = useState(false)
  const [selectedDirectionId, setSelectedDirectionId] = useState<string | null>(null)
  const [newName, setNewName] = useState("")
  const [createError, setCreateError] = useState<string | null>(null)
  const directions = useVisualDirections(includeArchived)
  const detail = useVisualDirection(selectedDirectionId)
  const mutations = useVisualDirectionMutations()

  useEffect(() => {
    if (!selectedDirectionId && directions.data?.directions[0]) {
      setSelectedDirectionId(directions.data.directions[0].direction.id)
    }
  }, [directions.data, selectedDirectionId])

  const create = async (event: FormEvent) => {
    event.preventDefault()
    const name = newName.trim()
    if (!name) {
      return
    }
    setCreateError(null)
    try {
      const created = await mutations.create.mutateAsync(name)
      setNewName("")
      setSelectedDirectionId(created.direction.id)
    } catch (cause) {
      setCreateError(
        cause instanceof Error ? cause.message : "Visual Direction could not be created"
      )
    }
  }

  return (
    <div className="grid min-h-[34rem] gap-5 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <aside className="rounded-xl border border-border bg-card p-3 shadow-card">
        <div className="mb-3 flex items-center justify-between gap-2 px-1">
          <div>
            <h2 className="text-sm font-semibold">Visual Directions</h2>

            <p className="mt-0.5 text-xs text-muted-foreground">Private, reusable visual systems</p>
          </div>

          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            Archived
          </label>
        </div>

        <form className="mb-3 flex gap-2" onSubmit={(event) => void create(event)}>
          <TextInput
            aria-label="New Visual Direction name"
            placeholder="New direction name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />

          <Button type="submit" size="sm" variant="primary" disabled={mutations.create.isPending}>
            <PackagePlus className="size-4" />
            Create
          </Button>
        </form>

        <FormField
          id="direction-preview-config-sku"
          label="Preview product"
          hint="URL-only context. It is never saved to the direction."
          className="mb-3"
        >
          {(props) => (
            <TextInput
              id="direction-preview-config-sku"
              placeholder="Config SKU"
              value={previewConfigSku}
              onChange={(event) => onPreviewConfigSkuChange(event.target.value)}
              {...props}
            />
          )}
        </FormField>

        {createError ? <p className="px-2 pb-3 text-xs text-destructive">{createError}</p> : null}

        {directions.isLoading ? (
          <p className="px-2 py-8 text-sm text-muted-foreground">Loading directions…</p>
        ) : null}

        {directions.error ? (
          <p className="px-2 py-4 text-sm text-destructive">{directions.error.message}</p>
        ) : null}

        {directions.data?.directions.length === 0 ? (
          <p className="rounded-lg bg-muted/50 px-3 py-6 text-sm text-muted-foreground">
            Create a direction to group reusable brand assets and canvas defaults.
          </p>
        ) : null}

        <div className="space-y-1">
          {directions.data?.directions.map(({ direction, draft }) => (
            <button
              key={direction.id}
              type="button"
              className={`w-full rounded-lg px-3 py-2.5 text-left transition-colors ${selectedDirectionId === direction.id ? "bg-foreground text-background" : "hover:bg-muted"}`}
              onClick={() => setSelectedDirectionId(direction.id)}
            >
              <span className="block truncate text-sm font-medium">{direction.name}</span>

              <span
                className={`mt-0.5 block text-xs ${selectedDirectionId === direction.id ? "text-background/65" : "text-muted-foreground"}`}
              >
                {directionDraftSummary(
                  draft.version,
                  draft.compatibleBrandFamilies.length,
                  direction.archivedAt !== null
                )}
              </span>
            </button>
          ))}
        </div>

        {directions.hasNextPage ? (
          <Button
            className="mt-3 w-full"
            size="sm"
            disabled={directions.isFetchingNextPage}
            onClick={() => void directions.fetchNextPage()}
          >
            {directions.isFetchingNextPage ? "Loading…" : "Load more directions"}
          </Button>
        ) : null}
      </aside>

      <main>
        {detail.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading Visual Direction…</p>
        ) : null}

        {detail.error ? (
          <Callout tone="destructive" title="Visual Direction failed to load">
            {detail.error.message}
          </Callout>
        ) : null}

        {detail.data ? (
          <VisualDirectionEditor
            key={`${detail.data.direction.id}:${detail.data.draft.version}`}
            visualDirection={detail.data}
            previewConfigSku={previewConfigSku}
            hasMoreVersions={detail.hasNextPage}
            isLoadingMoreVersions={detail.isFetchingNextPage}
            onLoadMoreVersions={() => void detail.fetchNextPage()}
          />
        ) : null}

        {!selectedDirectionId && !directions.isLoading ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center">
            <FileImage className="mx-auto size-8 text-muted-foreground" />

            <h2 className="mt-3 text-base font-semibold">No Visual Direction selected</h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Choose a direction or create the first one.
            </p>
          </div>
        ) : null}
      </main>
    </div>
  )
}

function VisualDirectionEditor({
  visualDirection,
  hasMoreVersions,
  isLoadingMoreVersions,
  onLoadMoreVersions,
  previewConfigSku,
}: {
  visualDirection: VisualDirection
  hasMoreVersions: boolean
  isLoadingMoreVersions: boolean
  onLoadMoreVersions: () => void
  previewConfigSku: string
}) {
  const mutations = useVisualDirectionMutations()
  const { direction, draft, versions } = visualDirection
  const [name, setName] = useState(direction.name)
  const [families, setFamilies] = useState(draft.compatibleBrandFamilies.join(", "))
  const [palettes, setPalettes] = useState<VisualDirectionPalette[]>(draft.palettes)
  const [selectedPaletteId, setSelectedPaletteId] = useState(
    draft.compositionDefaults.paletteId ?? ""
  )
  const [headingFont, setHeadingFont] = useState(draft.typographyDefaults.headingFontAssetId ?? "")
  const [bodyFont, setBodyFont] = useState(draft.typographyDefaults.bodyFontAssetId ?? "")
  const [logo, setLogo] = useState(draft.compositionDefaults.logoAssetId ?? "")
  const [foreground, setForeground] = useState(draft.compositionDefaults.foreground ?? "")
  const [background, setBackground] = useState(draft.compositionDefaults.background ?? "")
  const [accent, setAccent] = useState(draft.compositionDefaults.accent ?? "")
  const [generationGuidance, setGenerationGuidance] = useState(draft.generationGuidance ?? "")
  const [publishConfirmed, setPublishConfirmed] = useState(false)
  const [archiveConfirmed, setArchiveConfirmed] = useState(false)
  const [assetName, setAssetName] = useState("")
  const [assetKind, setAssetKind] = useState<VisualDirectionAssetKind>("logo")
  const [assetFile, setAssetFile] = useState<File | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const fonts = draft.assets.filter((asset) => asset.kind === "font")
  const logos = draft.assets.filter((asset) => asset.kind === "logo")
  const isArchived = direction.archivedAt !== null
  const proposalQuery = useVisualDirectionProposals(direction.id)

  const nextPublishedVersion = Math.max(0, ...versions.map((version) => version.version)) + 1
  const proposal = selectRecoveredProposal(proposalQuery.data, draft.version)

  const save = async (event: FormEvent) => {
    event.preventDefault()
    setFormError(null)
    try {
      await mutations.update.mutateAsync({
        directionId: direction.id,
        expectedVersion: draft.version,
        name,
        compatibleBrandFamilies: families
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        palettes,
        typographyDefaults: {
          headingFontAssetId: headingFont || null,
          bodyFontAssetId: bodyFont || null,
        },
        compositionDefaults: {
          paletteId: selectedPaletteId || null,
          logoAssetId: logo || null,
          foreground: foreground || null,
          background: background || null,
          accent: accent || null,
        },
        generationGuidance: generationGuidance.trim() || null,
      })
    } catch (cause) {
      setFormError(cause instanceof Error ? cause.message : "Visual Direction could not be saved")
    }
  }

  const upload = async (event: FormEvent) => {
    event.preventDefault()
    if (!assetFile || !assetName.trim()) {
      return
    }
    setFormError(null)
    try {
      await mutations.upload.mutateAsync({
        directionId: direction.id,
        expectedVersion: draft.version,
        name: assetName.trim(),
        kind: assetKind,
        mediaType: assetKind === "font" ? "font/woff2" : assetFile.type,
        bytesBase64: await fileBase64(assetFile),
      })
      setAssetName("")
      setAssetFile(null)
    } catch (cause) {
      setFormError(cause instanceof Error ? cause.message : "Asset upload failed")
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">{direction.name}</h1>

            {isArchived ? (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">Archived</span>
            ) : null}
          </div>

          <p className="mt-1 text-sm text-muted-foreground">
            {`Draft ${draft.version} · updated by ${draft.updatedBy}`}
          </p>
        </div>
      </header>

      {formError ? (
        <Callout tone="destructive" title="Visual Direction change failed">
          {formError}
        </Callout>
      ) : null}

      <VisualDirectionMoodboard
        directionId={direction.id}
        directionName={direction.name}
        draft={draft}
        proposal={proposal}
        previewConfigSku={previewConfigSku}
        onGenerate={() => {
          setFormError(null)
          mutations.generateProposal.mutate(
            { directionId: direction.id, expectedVersion: draft.version },
            {
              onError: (cause) => setFormError(cause.message),
            }
          )
        }}
        onApply={(proposalId) => {
          setFormError(null)
          mutations.applyProposal.mutate(
            { directionId: direction.id, proposalId },
            { onError: (cause) => setFormError(cause.message) }
          )
        }}
        onAbandon={(proposalId) => {
          setFormError(null)
          mutations.abandonProposal.mutate(
            { directionId: direction.id, proposalId },
            { onError: (cause) => setFormError(cause.message) }
          )
        }}
        isGenerating={mutations.generateProposal.isPending}
        isRecoveringProposal={proposalQuery.isLoading || proposalQuery.isFetching}
        isAbandoning={mutations.abandonProposal.isPending}
        isApplying={mutations.applyProposal.isPending}
        isArchived={isArchived}
      />

      <form
        className="rounded-xl border border-border bg-card p-5 shadow-card"
        onSubmit={(event) => void save(event)}
      >
        <div className="mb-5">
          <h2 className="text-sm font-semibold">Draft identity and defaults</h2>

          <p className="mt-1 text-xs text-muted-foreground">
            Published versions never change when this draft changes.
          </p>
        </div>

        <fieldset
          disabled={isArchived || mutations.update.isPending}
          className="grid gap-4 md:grid-cols-2"
        >
          <FormField id="visual-direction-name" label="Direction name">
            {(props) => (
              <TextInput
                id="visual-direction-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                {...props}
              />
            )}
          </FormField>

          <FormField
            id="brand-families"
            label="Compatible brand families"
            hint="Exact catalog families, separated by commas."
          >
            {(props) => (
              <TextInput
                id="brand-families"
                value={families}
                onChange={(event) => setFamilies(event.target.value)}
                {...props}
              />
            )}
          </FormField>

          <ColorSystemEditor
            palettes={palettes}
            selectedPaletteId={selectedPaletteId}
            onChange={(nextPalettes) => {
              setPalettes(nextPalettes)
              if (
                selectedPaletteId &&
                !nextPalettes.some((palette) => palette.id === selectedPaletteId)
              ) {
                setSelectedPaletteId("")
              }
            }}
            onSelectedPaletteIdChange={setSelectedPaletteId}
          />

          <AssetSelect
            id="heading-font"
            label="Heading font"
            value={headingFont}
            assets={fonts}
            onChange={setHeadingFont}
          />

          <AssetSelect
            id="body-font"
            label="Body font"
            value={bodyFont}
            assets={fonts}
            onChange={setBodyFont}
          />

          <AssetSelect
            id="default-logo"
            label="Default logo"
            value={logo}
            assets={logos}
            onChange={setLogo}
          />

          <div className="grid grid-cols-3 gap-2">
            <ColorField id="foreground" label="Text" value={foreground} onChange={setForeground} />

            <ColorField
              id="background"
              label="Background"
              value={background}
              onChange={setBackground}
            />

            <ColorField id="accent" label="Accent" value={accent} onChange={setAccent} />
          </div>

          <FormField
            id="generation-guidance"
            label="Generation guidance"
            hint="Creative direction for Gallery Studio backgrounds."
            className="md:col-span-2"
          >
            {(props) => (
              <textarea
                id="generation-guidance"
                rows={3}
                className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                value={generationGuidance}
                onChange={(event) => setGenerationGuidance(event.target.value)}
                {...props}
              />
            )}
          </FormField>
        </fieldset>

        {!isArchived ? (
          <Button className="mt-5" type="submit" variant="primary">
            Save draft
          </Button>
        ) : null}
      </form>

      <VisualDirectionAssetLibrary
        directionId={direction.id}
        draft={draft}
        isArchived={isArchived}
        assetName={assetName}
        assetKind={assetKind}
        assetFile={assetFile}
        mutations={mutations}
        onAssetNameChange={setAssetName}
        onAssetKindChange={setAssetKind}
        onAssetFileChange={setAssetFile}
        onUpload={upload}
        onError={setFormError}
      />

      <VisualDirectionLifecycle
        direction={direction}
        draft={draft}
        versions={versions}
        nextPublishedVersion={nextPublishedVersion}
        publishConfirmed={publishConfirmed}
        archiveConfirmed={archiveConfirmed}
        mutations={mutations}
        onPublishConfirmedChange={setPublishConfirmed}
        onArchiveConfirmedChange={setArchiveConfirmed}
        onError={setFormError}
        hasMoreVersions={hasMoreVersions}
        isLoadingMoreVersions={isLoadingMoreVersions}
        onLoadMoreVersions={onLoadMoreVersions}
      />
    </div>
  )
}

function selectRecoveredProposal(
  result: { proposals: VisualDirectionProposal[] } | undefined,
  draftVersion: number
): VisualDirectionProposal["proposal"] | null {
  const proposals = result ? result.proposals.map(({ proposal }) => proposal) : []
  const pending = proposals.find(({ status }) => status === "pending")
  if (pending) {
    return pending
  }
  const current = proposals.find(
    ({ status, baseDraftVersion }) => status === "succeeded" && baseDraftVersion === draftVersion
  )
  return current ?? proposals[0] ?? null
}

function proposalPreview(
  proposal: VisualDirectionProposal["proposal"] | null,
  directionName: string,
  draft: VisualDirectionDraft
): VisualDirectionPreview {
  return (
    proposal?.output ?? {
      name: directionName,
      compatibleBrandFamilies: draft.compatibleBrandFamilies,
      palettes: draft.palettes,
      typographyDefaults: draft.typographyDefaults,
      compositionDefaults: draft.compositionDefaults,
      generationGuidance: draft.generationGuidance,
    }
  )
}

function findAsset(
  assets: VisualDirectionAsset[],
  id: string | null
): VisualDirectionAsset | undefined {
  return id ? assets.find((asset) => asset.id === id) : undefined
}

function assetId(asset: VisualDirectionAsset | undefined): string | null {
  return asset?.id ?? null
}

function proposalPreviewId(
  proposal: VisualDirectionProposal["proposal"] | null
): string | undefined {
  return proposal?.status === "succeeded" && proposal.output ? proposal.id : undefined
}

function VisualDirectionMoodboard({
  directionId,
  directionName,
  draft,
  proposal,
  previewConfigSku,
  onGenerate,
  onAbandon,
  onApply,
  isGenerating,
  isRecoveringProposal,
  isAbandoning,
  isApplying,
  isArchived,
}: {
  directionId: string
  directionName: string
  draft: VisualDirectionDraft
  proposal: VisualDirectionProposal["proposal"] | null
  previewConfigSku: string
  onGenerate: () => void
  onAbandon: (proposalId: string) => void
  onApply: (proposalId: string) => void
  isGenerating: boolean
  isRecoveringProposal: boolean
  isAbandoning: boolean
  isApplying: boolean
  isArchived: boolean
}) {
  const preview = proposalPreview(proposal, directionName, draft)
  const headingFont = findAsset(draft.assets, preview.typographyDefaults.headingFontAssetId)
  const bodyFont = findAsset(draft.assets, preview.typographyDefaults.bodyFontAssetId)
  const headingCoverage = useVisualDirectionFontGlyphCoverage(assetId(headingFont))
  const bodyCoverage = useVisualDirectionFontGlyphCoverage(assetId(bodyFont))
  const references = draft.assets.filter((asset) => asset.use === "generation_reference")
  const treatments = draft.assets.filter((asset) => asset.use === "direct_composition")
  const sample = useVisualDirectionMoodboardSample(
    directionId,
    previewConfigSku,
    proposalPreviewId(proposal)
  )
  const foreground = preview.compositionDefaults.foreground ?? "#171717"
  const background = preview.compositionDefaults.background ?? "#F7F7F5"
  const accent = preview.compositionDefaults.accent ?? "#334BFA"
  const contrast = contrastRatio(foreground, background)

  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <MoodboardHeader
        proposal={proposal}
        referenceCount={references.length}
        isArchived={isArchived}
        isGenerating={isGenerating}
        isRecoveringProposal={isRecoveringProposal}
        draftVersion={draft.version}
        isAbandoning={isAbandoning}
        isApplying={isApplying}
        onGenerate={onGenerate}
        onAbandon={onAbandon}
        onApply={onApply}
      />

      {references.length === 0 ? (
        <Callout tone="warning" title="References required" dense>
          Upload at least one style or human-model reference. Uploads never trigger analysis.
        </Callout>
      ) : null}

      {headingFont ? (
        <style>{`@font-face{font-family:${JSON.stringify(`direction-heading-${headingFont.id}`)};src:url(${JSON.stringify(routesPathForAsset(headingFont.id))}) format("woff2");font-display:swap}`}</style>
      ) : null}

      {bodyFont ? (
        <style>{`@font-face{font-family:${JSON.stringify(`direction-body-${bodyFont.id}`)};src:url(${JSON.stringify(routesPathForAsset(bodyFont.id))}) format("woff2");font-display:swap}`}</style>
      ) : null}

      <div className="mt-5 grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-4">
          <MoodboardReferences references={references} />

          <MoodboardTypographySample
            preview={preview}
            headingFont={headingFont}
            bodyFont={bodyFont}
            foreground={foreground}
            background={background}
            accent={accent}
          />

          <MoodboardPalettes palettes={preview.palettes} />
        </div>

        <div className="space-y-4">
          <MoodboardProductSample previewConfigSku={previewConfigSku} sample={sample} />

          <MoodboardAssets treatments={treatments} />

          <MoodboardChecks
            contrast={contrast}
            fonts={[
              { label: "Heading", asset: headingFont, coverage: headingCoverage },
              { label: "Body", asset: bodyFont, coverage: bodyCoverage },
            ]}
          />
        </div>
      </div>

      {proposal?.output ? (
        <ProposalDiff directionName={directionName} draft={draft} proposal={proposal} />
      ) : null}
    </section>
  )
}

type VisualDirectionPreview = NonNullable<VisualDirectionProposal["proposal"]["output"]>

function MoodboardHeader({
  proposal,
  referenceCount,
  isArchived,
  isGenerating,
  isRecoveringProposal,
  draftVersion,
  isAbandoning,
  isApplying,
  onGenerate,
  onAbandon,
  onApply,
}: {
  proposal: VisualDirectionProposal["proposal"] | null
  referenceCount: number
  isArchived: boolean
  isGenerating: boolean
  isRecoveringProposal: boolean
  draftVersion: number
  isAbandoning: boolean
  isApplying: boolean
  onGenerate: () => void
  onAbandon: (proposalId: string) => void
  onApply: (proposalId: string) => void
}) {
  const [abandonConfirmed, setAbandonConfirmed] = useState(false)
  const state = moodboardHeaderState({
    proposal,
    draftVersion,
    isArchived,
    isGenerating,
    isRecoveringProposal,
    referenceCount,
  })
  const applyProposalId = state.applyProposalId
  useEffect(() => {
    setAbandonConfirmed(false)
  }, [proposal?.id, proposal?.status])
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold tracking-tight">Direction moodboard</h2>

          <ProposalBadge proposal={proposal} currentProposal={state.currentProposal} />
        </div>

        <p className="mt-1 text-xs text-muted-foreground">
          References lead the proposal. Review this exact draft before applying or publishing.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" disabled={state.generateDisabled} onClick={onGenerate}>
          <Sparkles className="size-4" />

          {proposalButtonLabel({
            isGenerating,
            pendingProposal: state.pendingProposal,
            isRecoveringProposal,
          })}
        </Button>

        {state.pendingProposal && proposal ? (
          <div className="flex flex-wrap items-center justify-end gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={abandonConfirmed}
                onChange={(event) => setAbandonConfirmed(event.target.checked)}
              />
              Confirm provider work may still finish and its result will be ignored.
            </label>

            <Button
              type="button"
              variant="destructive"
              disabled={!abandonConfirmed || isAbandoning}
              onClick={() => onAbandon(proposal.id)}
            >
              {isAbandoning ? "Abandoning…" : "Abandon pending proposal"}
            </Button>
          </div>
        ) : null}

        {applyProposalId ? (
          <Button
            type="button"
            variant="primary"
            disabled={isApplying}
            onClick={() => onApply(applyProposalId)}
          >
            {isApplying ? "Applying…" : "Apply whole proposal"}
          </Button>
        ) : null}
      </div>
    </header>
  )
}

function moodboardHeaderState(input: {
  proposal: VisualDirectionProposal["proposal"] | null
  draftVersion: number
  isArchived: boolean
  isGenerating: boolean
  isRecoveringProposal: boolean
  referenceCount: number
}) {
  const pendingProposal = input.proposal?.status === "pending"
  const currentProposal = input.proposal?.baseDraftVersion === input.draftVersion
  return {
    pendingProposal,
    currentProposal,
    applyProposalId:
      input.proposal?.status === "succeeded" && input.proposal.output && currentProposal
        ? input.proposal.id
        : null,
    generateDisabled:
      input.isArchived ||
      input.isGenerating ||
      input.isRecoveringProposal ||
      pendingProposal ||
      (input.proposal?.status === "succeeded" && currentProposal) ||
      input.referenceCount === 0,
  }
}

function ProposalBadge({
  proposal,
  currentProposal,
}: {
  proposal: VisualDirectionProposal["proposal"] | null
  currentProposal: boolean
}) {
  if (!proposal) {
    return null
  }
  let label = "Stale unapplied proposal"
  if (proposal.status === "pending") {
    label = "Proposal pending"
  } else if (currentProposal) {
    label = "Unapplied proposal"
  }
  return (
    <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-medium text-brand">
      {label}
    </span>
  )
}

function proposalButtonLabel(input: {
  isGenerating: boolean
  pendingProposal: boolean
  isRecoveringProposal: boolean
}): string {
  if (input.isGenerating || input.pendingProposal) {
    return "Analyzing references…"
  }
  return input.isRecoveringProposal ? "Checking proposals…" : "Generate proposal"
}

function MoodboardReferences({ references }: { references: VisualDirectionAsset[] }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {references.map((asset) => (
        <MoodboardReferenceCard key={asset.id} asset={asset} />
      ))}
    </div>
  )
}

function MoodboardReferenceCard({ asset }: { asset: VisualDirectionAsset }) {
  const label = `${asset.name} · ${asset.kind === "style_image" ? "Style" : "Human model"}`
  return (
    <figure className="overflow-hidden rounded-lg border border-border bg-muted/30">
      <img
        alt={asset.name}
        className="aspect-[4/3] w-full object-cover"
        src={routesPathForAsset(asset.id)}
      />

      <figcaption className="p-2 text-xs text-muted-foreground">{label}</figcaption>
    </figure>
  )
}

function MoodboardTypographySample({
  preview,
  headingFont,
  bodyFont,
  foreground,
  background,
  accent,
}: {
  preview: VisualDirectionPreview
  headingFont: VisualDirectionAsset | undefined
  bodyFont: VisualDirectionAsset | undefined
  foreground: string
  background: string
  accent: string
}) {
  return (
    <div
      className="rounded-xl border border-border p-5"
      style={{ color: foreground, backgroundColor: background }}
    >
      <p className="text-sm font-semibold">{preview.name}</p>

      <p className="text-xs opacity-70">
        {preview.compatibleBrandFamilies.join(" · ") || "Available for brands: none"}
      </p>

      <h3
        className="mt-3 text-3xl font-semibold tracking-tight"
        style={{ fontFamily: headingFont ? `direction-heading-${headingFont.id}` : undefined }}
      >
        Everyday strength, made clear.
      </h3>

      <p
        className="mt-3 max-w-xl text-sm"
        style={{ fontFamily: bodyFont ? `direction-body-${bodyFont.id}` : undefined }}
      >
        A longer marketplace copy case checks rhythm, wrapping, hierarchy, and how the treatment
        behaves when benefit text needs more than one line.
      </p>

      <span
        className="mt-5 inline-flex rounded-full px-3 py-1.5 text-xs font-medium"
        style={{ backgroundColor: accent, color: bestTextColor(accent) }}
      >
        Representative badge
      </span>
    </div>
  )
}

function MoodboardPalettes({ palettes }: { palettes: VisualDirectionPalette[] }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      {palettes.flatMap((palette) =>
        palette.colors.map((color) => (
          <div
            key={`${palette.id}:${color}`}
            className="flex items-center gap-2 rounded-lg border border-border p-2"
          >
            <span
              className="size-8 rounded-full border border-border"
              style={{ backgroundColor: color }}
            />

            <span className="text-xs">
              <span className="block font-medium">{palette.name}</span>

              <span className="text-muted-foreground">{color}</span>
            </span>
          </div>
        ))
      )}
    </div>
  )
}

function MoodboardProductSample({
  previewConfigSku,
  sample,
}: {
  previewConfigSku: string
  sample: ReturnType<typeof useVisualDirectionMoodboardSample>
}) {
  let content = (
    <p className="mt-2 text-xs text-muted-foreground">
      Add one config SKU in the URL context to preview its unchanged packshot.
    </p>
  )
  if (previewConfigSku.trim() && sample.data) {
    content = (
      <div className="mt-3 rounded-lg bg-card p-4 text-center">
        <div
          aria-label={`${previewConfigSku} unchanged packshot in Gallery Canvas`}
          className="aspect-square w-full overflow-hidden [&>[data-gallery-canvas-root]]:!h-full [&>[data-gallery-canvas-root]]:!w-full"
        >
          <GalleryCanvasScene request={sample.data} />
        </div>

        <p className="mt-2 font-mono text-xs text-muted-foreground">
          {previewConfigSku} · unchanged source pixels · Gallery Canvas
        </p>
      </div>
    )
  } else if (previewConfigSku.trim()) {
    content = (
      <p
        className={
          sample.error ? "mt-2 text-xs text-destructive" : "mt-2 text-xs text-muted-foreground"
        }
      >
        {sample.isLoading
          ? "Building ephemeral Gallery Canvas sample…"
          : (sample.error?.message ?? "No current main packshot found.")}
      </p>
    )
  }
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-4">
      <p className="text-xs font-medium">Ephemeral product sample</p>

      {content}

      <p className="mt-3 text-xs text-muted-foreground">
        This preview saves no Gallery revision, candidate, or layer edit.
      </p>
    </div>
  )
}

function MoodboardAssets({ treatments }: { treatments: VisualDirectionAsset[] }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <p className="text-xs font-medium">Direction assets</p>

      <ul className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        {treatments.length > 0 ? (
          treatments.map((asset) => (
            <li key={asset.id} className="rounded-lg border border-border p-2">
              {asset.kind !== "font" ? (
                <img
                  alt={asset.name}
                  className="mb-2 aspect-square w-full object-contain"
                  src={routesPathForAsset(asset.id)}
                />
              ) : null}

              <span className="block text-foreground">{asset.name}</span>

              <span>{asset.kind.replaceAll("_", " ")}</span>
            </li>
          ))
        ) : (
          <li>No logo, icon, badge, decoration, or font selected.</li>
        )}
      </ul>
    </div>
  )
}

function MoodboardChecks({
  contrast,
  fonts,
}: {
  contrast: number
  fonts: {
    label: string
    asset: VisualDirectionAsset | undefined
    coverage: ReturnType<typeof useVisualDirectionFontGlyphCoverage>
  }[]
}) {
  const contrastPasses = contrast >= 4.5
  const contrastLabel = `Text/background contrast: ${contrast.toFixed(2)}:1 · ${contrastPasses ? "pass" : "warning"}`
  return (
    <div className="rounded-xl border border-border p-4 text-xs">
      <p className="font-medium">Checks</p>

      <p className={contrastPasses ? "mt-2 text-success" : "mt-2 text-warning"}>{contrastLabel}</p>

      {fonts.some(({ asset }) => asset) ? (
        fonts.flatMap(({ label, asset, coverage }) => {
          if (!asset) {
            return []
          }
          if (coverage.isLoading) {
            return (
              <p className="mt-2 text-muted-foreground" key={label}>{`${label} cmap: checking…`}</p>
            )
          }
          if (coverage.isError || !coverage.data) {
            return <p className="mt-2 text-warning" key={label}>{`${label} cmap: check failed.`}</p>
          }
          const missing = coverage.data.missingCodePoints
          return (
            <p
              className={missing.length === 0 ? "mt-2 text-success" : "mt-2 text-warning"}
              key={label}
            >
              {missing.length === 0
                ? `${label} cmap: sample coverage passes.`
                : `${label} cmap missing ${missing.map(formatCodePoint).join(", ")} from “${coverage.data.sample}”.`}
            </p>
          )
        })
      ) : (
        <p className="mt-2 text-muted-foreground">Glyph coverage: no direction font selected.</p>
      )}
    </div>
  )
}

function formatCodePoint(codePoint: number): string {
  return `U+${codePoint.toString(16).toUpperCase().padStart(4, "0")}`
}

function ProposalDiff({
  directionName,
  draft,
  proposal,
}: {
  directionName: string
  draft: VisualDirectionDraft
  proposal: VisualDirectionProposal["proposal"]
}) {
  const output = proposal.output
  if (!output) {
    return null
  }
  const rows = [
    ["Direction name", directionName, output.name],
    ["Available for brands", draft.compatibleBrandFamilies, output.compatibleBrandFamilies],
    ["Color system", draft.palettes, output.palettes],
    ["Typography", draft.typographyDefaults, output.typographyDefaults],
    ["Composition", draft.compositionDefaults, output.compositionDefaults],
    ["Generation guidance", draft.generationGuidance, output.generationGuidance],
  ] as const
  const provenance = `${proposal.provider} · ${proposal.model} · ${proposal.promptVersion} · draft ${proposal.baseDraftVersion}`
  return (
    <div className="mt-5 rounded-xl border border-border">
      <div className="border-b border-border px-4 py-3">
        <p className="text-xs font-medium">Complete proposal diff</p>

        <p className="mt-1 text-xs text-muted-foreground">{provenance}</p>
      </div>

      <dl className="divide-y divide-border">
        {rows.map(([label, before, after]) => (
          <div key={label} className="grid gap-2 px-4 py-3 text-xs md:grid-cols-[10rem_1fr_1fr]">
            <dt className="font-medium">{label}</dt>

            <dd className="break-words text-muted-foreground">Before: {JSON.stringify(before)}</dd>

            <dd className="break-words">Proposed: {JSON.stringify(after)}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

type VisualDirectionMutations = ReturnType<typeof useVisualDirectionMutations>

function VisualDirectionAssetLibrary({
  directionId,
  draft,
  isArchived,
  assetName,
  assetKind,
  assetFile,
  mutations,
  onAssetNameChange,
  onAssetKindChange,
  onAssetFileChange,
  onUpload,
  onError,
}: {
  directionId: string
  draft: VisualDirectionDraft
  isArchived: boolean
  assetName: string
  assetKind: VisualDirectionAssetKind
  assetFile: File | null
  mutations: VisualDirectionMutations
  onAssetNameChange: (value: string) => void
  onAssetKindChange: (value: VisualDirectionAssetKind) => void
  onAssetFileChange: (value: File | null) => void
  onUpload: (event: FormEvent) => Promise<void>
  onError: (message: string) => void
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <h2 className="text-sm font-semibold">Private assets</h2>

      <p className="mt-1 text-xs text-muted-foreground">
        Fonts and artwork compose directly. Only style and model references can reach generation.
      </p>

      {!isArchived ? (
        <form
          className="mt-4 grid gap-3 md:grid-cols-[1fr_12rem_1.4fr_auto]"
          onSubmit={(event) => void onUpload(event)}
        >
          <TextInput
            aria-label="Asset name"
            placeholder="Asset name"
            value={assetName}
            onChange={(event) => onAssetNameChange(event.target.value)}
          />

          <Combobox
            aria-label="Asset kind"
            value={assetKind}
            options={ASSET_KINDS}
            searchPlaceholder="Search asset kinds…"
            emptyMessage="No asset kinds"
            onChange={(value) => {
              if (value !== undefined) {
                onAssetKindChange(value as VisualDirectionAssetKind)
              }
            }}
          />

          <input
            aria-label="Asset file"
            type="file"
            accept={assetAccept(assetKind)}
            onChange={(event) => onAssetFileChange(event.target.files?.[0] ?? null)}
            className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-muted file:px-3 file:py-2 file:text-xs"
          />

          <Button
            type="submit"
            disabled={!assetFile || !assetName.trim() || mutations.upload.isPending}
          >
            <Upload className="size-4" />
            Upload
          </Button>
        </form>
      ) : null}

      <ul className="mt-4 divide-y divide-border">
        {draft.assets.map((asset) => (
          <li key={asset.id} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{asset.name}</p>

              <p className="text-xs text-muted-foreground">{visualDirectionAssetSummary(asset)}</p>
            </div>

            <div className="flex items-center gap-2">
              {asset.kind !== "font" ? (
                <a
                  className="text-xs text-info hover:underline"
                  href={routesPathForAsset(asset.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Preview
                </a>
              ) : null}

              {!isArchived ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    void mutations.removeAsset
                      .mutateAsync({
                        directionId,
                        assetId: asset.id,
                        expectedVersion: draft.version,
                      })
                      .catch((cause: Error) => onError(cause.message))
                  }
                >
                  Remove
                </Button>
              ) : null}
            </div>
          </li>
        ))}

        {draft.assets.length === 0 ? (
          <li className="py-6 text-sm text-muted-foreground">
            No assets uploaded. Palettes can still be published without binaries.
          </li>
        ) : null}
      </ul>
    </section>
  )
}

function VisualDirectionLifecycle({
  direction,
  draft,
  versions,
  nextPublishedVersion,
  publishConfirmed,
  archiveConfirmed,
  mutations,
  onPublishConfirmedChange,
  onArchiveConfirmedChange,
  onError,
  hasMoreVersions,
  isLoadingMoreVersions,
  onLoadMoreVersions,
}: {
  direction: VisualDirection["direction"]
  draft: VisualDirectionDraft
  versions: VisualDirectionVersion[]
  nextPublishedVersion: number
  publishConfirmed: boolean
  archiveConfirmed: boolean
  mutations: VisualDirectionMutations
  onPublishConfirmedChange: (confirmed: boolean) => void
  onArchiveConfirmedChange: (confirmed: boolean) => void
  onError: (message: string) => void
  hasMoreVersions: boolean
  isLoadingMoreVersions: boolean
  onLoadMoreVersions: () => void
}) {
  const isArchived = direction.archivedAt !== null
  return (
    <>
      {!isArchived ? (
        <Callout tone="info" icon={<Check className="size-4" />} title="Publish immutable version">
          <PublishSnapshot version={nextPublishedVersion} draft={draft} />

          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={publishConfirmed}
              onChange={(event) => onPublishConfirmedChange(event.target.checked)}
            />{" "}
            I reviewed this exact version summary.
          </label>

          <Button
            className="mt-3"
            variant="primary"
            disabled={!publishConfirmed || mutations.publish.isPending}
            onClick={() =>
              void mutations.publish
                .mutateAsync({ directionId: direction.id, expectedVersion: draft.version })
                .then(() => onPublishConfirmedChange(false))
                .catch((cause: Error) => onError(cause.message))
            }
          >
            Publish version
          </Button>
        </Callout>
      ) : null}

      <PublishedVisualDirectionVersions
        directionId={direction.id}
        versions={versions}
        mutations={mutations}
        onError={onError}
        hasMoreVersions={hasMoreVersions}
        isLoadingMoreVersions={isLoadingMoreVersions}
        onLoadMoreVersions={onLoadMoreVersions}
      />

      {!isArchived ? (
        <Callout
          tone="warning"
          icon={<Archive className="size-4" />}
          title="Archive Visual Direction"
        >
          <p className="mt-2 text-sm text-muted-foreground">
            Existing versions and candidates stay reproducible. New selections stop.
          </p>

          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={archiveConfirmed}
              onChange={(event) => onArchiveConfirmedChange(event.target.checked)}
            />{" "}
            I understand this direction becomes read-only.
          </label>

          <Button
            className="mt-3"
            variant="destructive"
            disabled={!archiveConfirmed || mutations.archive.isPending}
            onClick={() =>
              void mutations.archive
                .mutateAsync(direction.id)
                .catch((cause: Error) => onError(cause.message))
            }
          >
            Archive direction
          </Button>
        </Callout>
      ) : null}
    </>
  )
}

function PublishedVisualDirectionVersions({
  directionId,
  versions,
  mutations,
  onError,
  hasMoreVersions,
  isLoadingMoreVersions,
  onLoadMoreVersions,
}: {
  directionId: string
  versions: VisualDirectionVersion[]
  mutations: VisualDirectionMutations
  onError: (message: string) => void
  hasMoreVersions: boolean
  isLoadingMoreVersions: boolean
  onLoadMoreVersions: () => void
}) {
  const [archiveConfirmedVersionId, setArchiveConfirmedVersionId] = useState<string | null>(null)
  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <h2 className="text-sm font-semibold">Published versions</h2>

      <div className="mt-3 space-y-3">
        {versions.map((version) => (
          <article key={version.id} className="rounded-lg border border-border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{versionTitle(version)}</p>

                <p className="mt-1 text-xs text-muted-foreground">{versionSummary(version)}</p>
              </div>

              {!version.archivedAt ? (
                <div className="space-y-2 text-right">
                  <label className="flex items-center justify-end gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={archiveConfirmedVersionId === version.id}
                      onChange={(event) =>
                        setArchiveConfirmedVersionId(event.target.checked ? version.id : null)
                      }
                    />

                    <span>Confirm archive v{version.version}</span>
                  </label>

                  <Button
                    size="sm"
                    disabled={archiveConfirmedVersionId !== version.id}
                    onClick={() =>
                      void mutations.archiveVersion
                        .mutateAsync({ directionId, versionId: version.id })
                        .then(() => setArchiveConfirmedVersionId(null))
                        .catch((cause: Error) => onError(cause.message))
                    }
                  >
                    Archive version
                  </Button>
                </div>
              ) : null}
            </div>

            {!version.archivedAt ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {version.compatibleBrandFamilies.map((family) => (
                  <Button
                    key={family}
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void mutations.setDefault
                        .mutateAsync({ directionId, brandFamily: family, versionId: version.id })
                        .catch((cause: Error) => onError(cause.message))
                    }
                  >
                    {`Set v${version.version} default for ${family}`}
                  </Button>
                ))}
              </div>
            ) : null}
          </article>
        ))}

        {versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No published versions yet.</p>
        ) : null}

        {hasMoreVersions ? (
          <Button disabled={isLoadingMoreVersions} onClick={onLoadMoreVersions}>
            {isLoadingMoreVersions ? "Loading…" : "Load more versions"}
          </Button>
        ) : null}
      </div>
    </section>
  )
}

function AssetSelect({
  id,
  label,
  value,
  assets,
  onChange,
}: {
  id: string
  label: string
  value: string
  assets: { id: string; name: string }[]
  onChange: (value: string) => void
}) {
  return (
    <FormField id={id} label={label}>
      {(props) => (
        <Combobox
          id={id}
          aria-label={label}
          value={value || undefined}
          options={assets.map((asset) => ({ value: asset.id, label: asset.name }))}
          placeholder="None"
          clearLabel="None"
          searchPlaceholder={`Search ${label.toLowerCase()}…`}
          emptyMessage="No assets"
          onChange={(nextValue) => onChange(nextValue ?? "")}
          {...props}
        />
      )}
    </FormField>
  )
}

function ColorField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <FormField id={id} label={label}>
      {(props) => (
        <div className="flex gap-2">
          <input
            aria-label={`${label} color picker`}
            className="h-9 w-11 cursor-pointer rounded-lg border border-input bg-card p-1"
            type="color"
            value={validHex(value) ? value : "#000000"}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
          />

          <TextInput
            id={id}
            aria-invalid={value !== "" && !validHex(value)}
            placeholder="#112233"
            value={value}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            {...props}
          />
        </div>
      )}
    </FormField>
  )
}

function ColorSystemEditor({
  palettes,
  selectedPaletteId,
  onChange,
  onSelectedPaletteIdChange,
}: {
  palettes: VisualDirectionPalette[]
  selectedPaletteId: string
  onChange: (palettes: VisualDirectionPalette[]) => void
  onSelectedPaletteIdChange: (paletteId: string) => void
}) {
  const replacePalette = (index: number, palette: VisualDirectionPalette) =>
    onChange(palettes.map((current, currentIndex) => (currentIndex === index ? palette : current)))
  return (
    <div className="space-y-3 md:col-span-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Color system</p>

          <p className="text-xs text-muted-foreground">
            Edit, reorder, or remove deterministic swatches.
          </p>
        </div>

        <Button
          type="button"
          size="sm"
          onClick={() =>
            onChange([
              ...palettes,
              {
                id: `palette-${palettes.length + 1}`,
                name: `Palette ${palettes.length + 1}`,
                colors: ["#000000"],
              },
            ])
          }
        >
          Add palette
        </Button>
      </div>

      <FormField
        id="composition-palette"
        label="Composition palette"
        hint="The explicit palette used by Gallery Canvas defaults."
      >
        {(props) => (
          <Combobox
            id="composition-palette"
            aria-label="Composition palette"
            value={selectedPaletteId || undefined}
            options={palettes.map((palette) => ({ value: palette.id, label: palette.name }))}
            placeholder="None"
            clearLabel="None"
            searchPlaceholder="Search palettes…"
            emptyMessage="No palettes"
            onChange={(value) => onSelectedPaletteIdChange(value ?? "")}
            {...props}
          />
        )}
      </FormField>

      {palettes.map((palette, paletteIndex) => (
        <div key={palette.id} className="rounded-lg border border-border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <TextInput
              aria-label={`Palette ${paletteIndex + 1} name`}
              value={palette.name}
              onChange={(event) =>
                replacePalette(paletteIndex, {
                  ...palette,
                  name: event.target.value,
                })
              }
            />

            <Button
              type="button"
              size="sm"
              disabled={paletteIndex === 0}
              onClick={() => onChange(moveItem(palettes, paletteIndex, paletteIndex - 1))}
            >
              Earlier
            </Button>

            <Button
              type="button"
              size="sm"
              disabled={paletteIndex === palettes.length - 1}
              onClick={() => onChange(moveItem(palettes, paletteIndex, paletteIndex + 1))}
            >
              Later
            </Button>

            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={() => onChange(palettes.filter((_, index) => index !== paletteIndex))}
            >
              Remove
            </Button>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {palette.colors.map((color, colorIndex) => (
              <div key={`${palette.id}:${colorIndex}`} className="flex items-center gap-2">
                <input
                  aria-label={`${palette.name} swatch ${colorIndex + 1}`}
                  className="h-9 w-11 cursor-pointer rounded-lg border border-input bg-card p-1"
                  type="color"
                  value={validHex(color) ? color : "#000000"}
                  onChange={(event) =>
                    replacePalette(paletteIndex, {
                      ...palette,
                      colors: palette.colors.map((current, index) =>
                        index === colorIndex ? event.target.value.toUpperCase() : current
                      ),
                    })
                  }
                />

                <TextInput
                  aria-label={`${palette.name} hex ${colorIndex + 1}`}
                  aria-invalid={!validHex(color)}
                  value={color}
                  onChange={(event) =>
                    replacePalette(paletteIndex, {
                      ...palette,
                      colors: palette.colors.map((current, index) =>
                        index === colorIndex ? event.target.value.toUpperCase() : current
                      ),
                    })
                  }
                />

                <Button
                  aria-label={`Remove ${palette.name} swatch ${colorIndex + 1}`}
                  type="button"
                  size="sm"
                  disabled={palette.colors.length === 1}
                  onClick={() =>
                    replacePalette(paletteIndex, {
                      ...palette,
                      colors: palette.colors.filter((_, index) => index !== colorIndex),
                    })
                  }
                >
                  ×
                </Button>
              </div>
            ))}
          </div>

          <Button
            className="mt-3"
            type="button"
            size="sm"
            disabled={palette.colors.length >= 16}
            onClick={() =>
              replacePalette(paletteIndex, { ...palette, colors: [...palette.colors, "#000000"] })
            }
          >
            Add swatch
          </Button>
        </div>
      ))}

      {palettes.length === 0 ? (
        <p className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
          No colors. Generate a reference-led proposal or add a palette manually.
        </p>
      ) : null}
    </div>
  )
}

function validHex(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value)
}

function moveItem<T>(items: T[], from: number, to: number): T[] {
  const next = [...items]
  const [item] = next.splice(from, 1)
  if (item !== undefined) {
    next.splice(to, 0, item)
  }
  return next
}

function contrastRatio(foreground: string, background: string): number {
  if (!validHex(foreground) || !validHex(background)) {
    return 0
  }
  const luminance = (hex: string) => {
    const channels = [1, 3, 5].map(
      (start) => Number.parseInt(hex.slice(start, start + 2), 16) / 255
    )
    const [red = 0, green = 0, blue = 0] = channels.map((channel) =>
      channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
    )
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue
  }
  const first = luminance(foreground)
  const second = luminance(background)
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

function bestTextColor(background: string): string {
  return contrastRatio("#FFFFFF", background) >= contrastRatio("#111111", background)
    ? "#FFFFFF"
    : "#111111"
}

function fileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const encoded = typeof reader.result === "string" ? reader.result.split(",", 2)[1] : undefined
      if (encoded) {
        resolve(encoded)
      } else {
        reject(new Error("Asset file could not be encoded"))
      }
    }
    reader.onerror = () => reject(reader.error ?? new Error("Asset file could not be read"))
    reader.readAsDataURL(file)
  })
}

function assetAccept(kind: VisualDirectionAssetKind): string {
  if (kind === "font") {
    return ".woff2,font/woff2"
  }
  if (kind === "style_image" || kind === "model_reference") {
    return "image/png,image/jpeg,image/webp"
  }
  return "image/png,image/jpeg,image/svg+xml"
}

function directionDraftSummary(version: number, familyCount: number, archived: boolean): string {
  return `Draft ${version} · ${familyCount} families${archived ? " · Archived" : ""}`
}

function visualDirectionAssetSummary(asset: VisualDirectionAsset): string {
  return `${asset.kind.replaceAll("_", " ")} · ${asset.use.replaceAll("_", " ")} · ${formatBytes(asset.byteSize)} · ${asset.byteHash.slice(0, 12)}`
}

function PublishSnapshot({ version, draft }: { version: number; draft: VisualDirectionDraft }) {
  const assetName = (id: string | null) =>
    id ? (draft.assets.find((asset) => asset.id === id)?.name ?? id) : "None"
  return (
    <div className="mt-3 rounded-lg bg-card/70 p-3 text-xs text-foreground">
      <p className="font-medium">Version {version} exact snapshot</p>

      <dl className="mt-2 grid gap-2">
        <div>
          <dt className="text-muted-foreground">Families</dt>

          <dd>{draft.compatibleBrandFamilies.join(", ") || "None"}</dd>
        </div>

        <div>
          <dt className="text-muted-foreground">Palettes and colors</dt>

          <dd>
            {draft.palettes.length > 0
              ? draft.palettes
                  .map((palette) => `${palette.name} (${palette.id}): ${palette.colors.join(", ")}`)
                  .join(" · ")
              : "None"}
          </dd>
        </div>

        <div>
          <dt className="text-muted-foreground">Typography</dt>

          <dd>{`Heading: ${assetName(draft.typographyDefaults.headingFontAssetId)} · Body: ${assetName(draft.typographyDefaults.bodyFontAssetId)}`}</dd>
        </div>

        <div>
          <dt className="text-muted-foreground">Composition defaults</dt>

          <dd>{`Palette: ${draft.compositionDefaults.paletteId ?? "None"} · Logo: ${assetName(draft.compositionDefaults.logoAssetId)} · Foreground: ${draft.compositionDefaults.foreground ?? "None"} · Background: ${draft.compositionDefaults.background ?? "None"} · Accent: ${draft.compositionDefaults.accent ?? "None"}`}</dd>
        </div>

        <div>
          <dt className="text-muted-foreground">Assets</dt>

          <dd>
            {draft.assets.length > 0
              ? draft.assets
                  .map(
                    (asset) =>
                      `${asset.name} · ${asset.kind.replaceAll("_", " ")} · ${asset.use.replaceAll("_", " ")} · ${asset.byteHash}`
                  )
                  .join(" | ")
              : "None"}
          </dd>
        </div>

        <div>
          <dt className="text-muted-foreground">Generation guidance</dt>

          <dd>{draft.generationGuidance ?? "None"}</dd>
        </div>
      </dl>
    </div>
  )
}

function versionTitle(version: VisualDirectionVersion): string {
  return `Version ${version.version}${version.archivedAt ? " · Archived" : ""}`
}

function versionSummary(version: VisualDirectionVersion): string {
  return `${version.compatibleBrandFamilies.join(", ")} · ${version.assets.length} assets · by ${version.publishedBy}`
}

function formatBytes(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.ceil(bytes / 1024)} KiB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MiB`
}

function routesPathForAsset(assetId: string): string {
  return `/api/visual-directions/assets/${encodeURIComponent(assetId)}/content`
}
